<script lang="ts">
  import { getContext } from "svelte"
  import { fly, slide } from "svelte/transition"
  import { DrawerStates } from "../constants/DrawerStates"
  import collapsed from "../Icons/collapsed.svg?raw"
  import chevronDown from "../Icons/chevron_down.svg?raw"
  import DrawerItem from "./DrawerItem.svelte"
  import type { Theme } from "website_script/types/theme"
  import type { Environment } from "website_script/types/environment"
  import { EVENT_TYPE } from "website_script/lib/events"

  const { trackFunction } = getContext<Environment>("environment")
  const { accentColor } = getContext<Theme>("theme")

  /**
   * In isCollapsable is true it will add a minification button to the bottom of the drawer.
   * In some cases, if there isn't a lot of items in the drawer, it is fine to never collapse it - isCollapsable = false.
   */
  export let isCollapsable: boolean = false
  export let shouldMinimizeDrawer: boolean = false
  export let handlePromptClose: () => void
  export let handleDrawerMinimized: () => void
  export let handleDrawerOpened: () => void
  // in the single button/single message version of the engagement widget, we want the prompt to stand alone, until
  // any interaction with the widget has happened.
  export let hide: boolean = false

  // We get current widgetState before we run a delay to show the widget and keep it in variable
  let currentWidgetStateBasedOnLoad: DrawerStates =
    shouldMinimizeDrawer && isCollapsable
      ? DrawerStates.COLLAPSED
      : DrawerStates.EXPANDED

  // We will be temporarily setting widgetState to null, but we need to keep the value
  // Therefore there is repeated state value in widgetState & nextWidgetState
  // Initially set to null to not display anything
  let widgetState: DrawerStates | null = null
  let nextWidgetState: DrawerStates | null

  widgetState = currentWidgetStateBasedOnLoad
  nextWidgetState = widgetState
  // Track widget showing
  trackFunction(
    `Seen Engagement Widget${isCollapsable ? ` - ${widgetState}` : ""}`,
    EVENT_TYPE.DISPLAYED_CTA,
    "engagement_widget"
  )

  /** Updates nextWidgetState with the value, temporarily sets widgetState to null */
  export const transitionTo = (newState: DrawerStates) => {
    if (nextWidgetState === newState) return
    nextWidgetState = newState
    widgetState = null
  }

  /**
   * When transition is over,
   * sets widgetState to nextWidgetState which has been set in transitionTo function
   * Prevents content jump between swapped elements
   */
  const onOutro = () => {
    widgetState = nextWidgetState
  }

  /**
   * Check what is the widgetState
   * Mainly to be used outside of the Drawer component
   */
  export const getCurrentWidgetState = () => {
    return widgetState
  }
</script>

<div class="containerWithControl">
  {#if (widgetState === DrawerStates.EXPANDED || !isCollapsable) && !hide}
    <div in:fly|global={{ y: 400, duration: 350 }} out:slide|global on:outroend={onOutro}>
      <slot />
      {#if isCollapsable}
        <DrawerItem
          size="50px"
          iconBg="transparent"
          iconSrc={chevronDown}
          label="Minimize"
          iconColor={accentColor}
          showLabel={false}
          on:click={(e) => {
            e.preventDefault()
            handlePromptClose()
            transitionTo(DrawerStates.COLLAPSED)
            handleDrawerMinimized()
          }}
        />
      {/if}
    </div>
  {:else if widgetState === DrawerStates.COLLAPSED && !hide}
    <div in:fly|global={{ y: 400, duration: 400 }} out:slide|global on:outroend={onOutro}>
      <DrawerItem
        iconSrc={collapsed}
        label="Open widget"
        labelStyle="label-left"
        showLabel={false}
        on:click={(e) => {
          e.preventDefault()
          transitionTo(DrawerStates.EXPANDED)
          handleDrawerOpened()
        }}
      />
    </div>
  {/if}
</div>

<style>
  .containerWithControl {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    border-radius: 28px;
  }
</style>
